/**
 * Choosing and remembering a theme.
 *
 * Three states, not two: light, dark, and **system** — which is the default and
 * where most users stay. A two-state toggle silently overrides the preference
 * someone set at the OS level, and offers no way back to following it.
 */

export type ThemePreference = "light" | "dark" | "system";

/** What is actually rendered, once "system" has been resolved. */
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "hardauto.theme";
export const THEME_ATTRIBUTE = "data-theme";

/** Read the OS preference. */
export function systemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? systemTheme() : preference;
}

/**
 * Read the stored preference.
 *
 * Every access is guarded: `localStorage` throws outright in a Safari private
 * window and when a browser is set to block site data, and an exception here
 * would take down the whole application before it rendered a pixel. Storing a
 * theme is a convenience; failing to is not worth a blank page.
 *
 * (Note this is a *preference*, not a credential. The rule against
 * `localStorage` applies to tokens, which live in memory — see
 * `@hardauto/core-api`.)
 */
export function readStoredTheme(): ThemePreference {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    // Private mode, blocked site data, or a quota error. Fall through.
  }
  return "system";
}

export function storeTheme(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // The theme still applies for this page; it just will not be remembered.
  }
}

/**
 * Apply a preference to the document.
 *
 * "system" REMOVES the attribute rather than writing a resolved value. That is
 * what lets the CSS media query take over, so the page follows the OS live —
 * a user switching their machine to dark at sunset sees the app follow without
 * reloading. Writing the resolved value would freeze it until the next visit.
 */
export function applyTheme(preference: ThemePreference): ResolvedTheme {
  const root = document.documentElement;
  if (preference === "system") {
    root.removeAttribute(THEME_ATTRIBUTE);
  } else {
    root.setAttribute(THEME_ATTRIBUTE, preference);
  }

  const resolved = resolveTheme(preference);
  // Tells the browser which scheme to paint form controls, scrollbars and the
  // canvas behind the page in. Without it, a dark page keeps light scrollbars.
  root.style.colorScheme = resolved;
  return resolved;
}

/**
 * A script to run before first paint, inlined into the HTML document.
 *
 * Without it the page renders with the light palette and then switches, which
 * is the white flash every dark-mode site is judged by. It has to be
 * synchronous and inline — a module import arrives too late, by definition.
 *
 * Returned as a string rather than imported, so the same logic cannot drift
 * between the inline copy and the runtime one.
 */
export function themeBootstrapScript(): string {
  return `
(function () {
  try {
    var stored = localStorage.getItem(${JSON.stringify(THEME_STORAGE_KEY)});
    if (stored === "light" || stored === "dark") {
      document.documentElement.setAttribute(${JSON.stringify(THEME_ATTRIBUTE)}, stored);
      document.documentElement.style.colorScheme = stored;
      return;
    }
  } catch (e) {}
  var dark = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
})();
`.trim();
}
